/**
 * PlanAccessoriesEditor (PARTS-NATIVE-2) — the structured parts/accessories on a
 * customer's PLUS plan, by HOME (custno+locno). Set once here (pricebook or free
 * text), then auto-suggested onto the member's tune-up dispatch and rolled into
 * Parts for the Day. "Suggest from notes" SEEDS existing members by parsing their
 * dispatch-note history (the migration path); going forward they're picked from
 * the pricebook. The note parser also stays as the ongoing safety-net.
 *
 * PLAN-PARTS-SEED (34i91, Seth: "if we've done humi work as part of the plan,
 * select the humi pad"): ESC already keeps the plan's included parts as rows
 * (mirror_agr_parts — APR35 ×1, APR501 ×1 on 0074771). Those lead the list as
 * INCLUDED, resolved to the pricebook; the note parser only suggests consumables
 * (pads, media, filters, bulbs) — never a one-time part like the 5000's door.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, Package, Plus, Sparkles, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { extractTruckParts, humidifierPad, isPlanConsumable, planPartKind, PART_CHIP, type PartKind, type TruckPart } from "@/lib/truckParts";
import { classifyEquipment } from "@/lib/equipment";
import { useCustomerEquipment } from "./CustomerEquipment";
import { searchPricebook } from "@/features/items/pricebookSearch";
import { PricebookBrowser } from "@/features/items/PricebookBrowser";
import type { AgrPartRow } from "@/lib/agreement";

export interface PlanAccessory { id: string; item_id: string | null; label: string; qty: number; kind: string | null; source: string }
interface ItemHit { id: string; part_number: string | null; description: string | null }

/** An included part as the plan card shows it: the code, how many, what the pricebook calls it. */
export interface IncludedPart { code: string; qty: number; description: string | null; item_id: string | null; kind: PartKind; source: "esc" | "pegasus" }

export function usePlanAccessories(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["plan_accessories", custno, locno],
    enabled: !!custno,
    queryFn: async (): Promise<PlanAccessory[]> => {
      let q = supabase.from("pegasus_plan_accessory").select("id, item_id, label, qty, kind, source").eq("custno", custno!);
      q = locno ? q.eq("locno", locno) : q.is("locno", null);
      const { data, error } = await q.order("created_at");
      if (error) throw error;
      return (data ?? []) as PlanAccessory[];
    },
  });
}

/**
 * Resolve an ESC agreement's part rows to the pricebook (APR35 → "Water Panel
 * Evaporator #35 Pad"). Match on the part number with punctuation and spaces
 * ignored; a code the pricebook doesn't know still shows, by its code.
 */
export function useIncludedParts(parts: AgrPartRow[] | undefined) {
  const codes = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of parts ?? []) {
      const code = (p.part ?? "").trim().toUpperCase();
      if (!code) continue;
      m.set(code, (m.get(code) ?? 0) + (Number(p.quantity) || 1));
    }
    return [...m.entries()];
  }, [parts]);
  const key = codes.map(([c, q]) => `${c}:${q}`).join("|");
  return useQuery({
    queryKey: ["plan_included_parts", key],
    enabled: codes.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IncludedPart[]> => {
      const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const wanted = codes.map(([c]) => c);
      // Part numbers in the pricebook may carry dashes/spaces the ESC code lacks
      // (L86-469 vs L86469); pull by prefix-free ilike on each code, then match
      // on the normalized number.
      const hits = new Map<string, ItemHit>();
      await Promise.all(wanted.map(async (c) => {
        const { data } = await supabase.from("item").select("id, part_number, description")
          .ilike("part_number", `%${c.replace(/[^A-Za-z0-9]/g, "%")}%`).eq("archived", false).limit(6);
        for (const it of (data ?? []) as ItemHit[]) {
          if (it.part_number && norm(it.part_number) === norm(c) && !hits.has(c)) hits.set(c, it);
        }
      }));
      return codes.map(([code, qty]) => {
        const it = hits.get(code) ?? null;
        return { code, qty, description: it?.description ?? null, item_id: it?.id ?? null, kind: planPartKind(code, it?.description), source: "esc" as const };
      });
    },
  });
}

function useItemSearch(term: string) {
  return useQuery({
    queryKey: ["item_search_plan", term.trim().toLowerCase()],
    enabled: term.trim().length >= 2,
    staleTime: 30 * 1000,
    // PRICEBOOK-SEARCH-01: the one pricebook search (every word, sizes, dashes, best first).
    queryFn: async (): Promise<ItemHit[]> => (await searchPricebook(term, 8)) as ItemHit[],
  });
}

/** Derive candidate accessories from this home's dispatch-note history (the seed). */
function useNoteSuggestions(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["plan_acc_note_suggest", custno, locno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TruckPart[]> => {
      let q = supabase.from("v_dispatch_full").select("notes").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.order("appointment_date", { ascending: false }).limit(40);
      const map = new Map<string, TruckPart>();
      for (const r of (data ?? []) as { notes: string | null }[]) {
        for (const p of extractTruckParts(r.notes)) if (!map.has(p.key)) map.set(p.key, p);
      }
      return [...map.values()];
    },
  });
}

/** The plan's own included parts (ESC's agreement parts, or a Pegasus sale's lines) — read-only rows. */
export function IncludedPartRows({ parts, loading }: { parts: IncludedPart[]; loading?: boolean }) {
  if (loading && parts.length === 0) return <div className="h-10 animate-pulse rounded-xl bg-slate-50" />;
  if (parts.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {parts.map((p) => (
        <div key={p.code} className="flex items-center gap-3 rounded-xl border border-hairline px-3.5 py-2.5">
          {p.source === "esc" && <span className={cn("rounded-md border px-2 py-0.5 font-mono text-[12.5px] font-bold", PART_CHIP[p.kind])}>{p.code}</span>}
          <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
            {p.source === "esc" ? (p.description ?? "Plan part") : p.code}
            {p.qty > 1 && <span className="ml-1.5 text-slate-500">×{p.qty}</span>}
            {p.source === "pegasus" && p.description && <span className="ml-1.5 text-xs text-slate-400">{p.description}</span>}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
            <Check className="h-3 w-3" /> Included
          </span>
        </div>
      ))}
    </div>
  );
}

/** Pricebook names for codes typed or guessed (no item_id) — the same lookup the ESC parts use. */
function useCodeNames(codes: string[]) {
  const key = [...new Set(codes.map((c) => c.toUpperCase()))].sort().join("|");
  return useQuery({
    queryKey: ["plan_code_names", key],
    enabled: codes.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      const norm = (x: string) => x.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const out: Record<string, string> = {};
      await Promise.all([...new Set(codes)].map(async (c) => {
        if (!/^[A-Z0-9][A-Z0-9 -]{1,14}$/i.test(c)) return;
        const { data } = await supabase.from("item").select("part_number, description").ilike("part_number", `%${c.replace(/[^A-Za-z0-9]/g, "%")}%`).eq("archived", false).limit(6);
        for (const it of (data ?? []) as ItemHit[]) if (it.part_number && norm(it.part_number) === norm(c) && it.description) { out[norm(c)] = it.description; break; }
      }));
      return out;
    },
  });
}

export function PlanAccessoriesEditor({ custno, locno, included = [], includedLoading, embedded }: {
  custno: string;
  locno: string | null;
  /** The plan's own included parts — shown first as Included, excluded from suggestions. */
  included?: IncludedPart[];
  includedLoading?: boolean;
  /** Inside the plan card (no outer card chrome of its own). */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const { data: parts } = usePlanAccessories(custno, locno);
  const { data: suggestions } = useNoteSuggestions(custno, locno);
  const { data: equip } = useCustomerEquipment(custno, locno);
  const [term, setTerm] = useState("");
  const [browse, setBrowse] = useState(false);
  const { data: results } = useItemSearch(term);

  // If a humidifier is on this home's equipment, match the RIGHT pad from its model.
  const equipPad = useMemo<TruckPart | null>(() => {
    for (const r of equip ?? []) {
      if (classifyEquipment(r).category === "humidifier") {
        const pad = humidifierPad(r.model);
        if (pad) return { key: pad, label: pad, kind: "humidifier" };
      }
    }
    return null;
  }, [equip]);

  const inval = () => qc.invalidateQueries({ queryKey: ["plan_accessories", custno, locno] });
  const add = useMutation({
    mutationFn: async (v: { item_id: string | null; label: string; kind?: string | null; source?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_plan_accessory").insert({
        custno, locno, item_id: v.item_id, label: v.label, qty: 1, kind: v.kind ?? "part", source: v.source ?? "manual", created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => { setTerm(""); inval(); },
  });
  const setQty = useMutation({
    mutationFn: async (v: { id: string; qty: number }) => { const { error } = await supabase.from("pegasus_plan_accessory").update({ qty: v.qty }).eq("id", v.id); if (error) throw error; },
    onSuccess: inval,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("pegasus_plan_accessory").delete().eq("id", id); if (error) throw error; },
    onSuccess: inval,
  });

  const list = parts ?? [];
  const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const { data: codeNames } = useCodeNames(list.filter((p) => !p.item_id).map((p) => p.label));
  const have = new Set<string>([...list.map((p) => norm(p.label)), ...included.map((p) => norm(p.code))]);
  const includedHasPad = included.some((p) => p.kind === "humidifier");
  // Merge note-derived parts with the equipment-matched humidifier pad, dedupe,
  // and keep only consumables a plan replaces (PLAN-PARTS-SEED).
  const pool = new Map<string, TruckPart>();
  for (const s of suggestions ?? []) if (isPlanConsumable(s)) pool.set(norm(s.label), s);
  if (equipPad && !includedHasPad) pool.set(norm(equipPad.label), equipPad);
  const newSuggestions = [...pool.values()].filter((s) => !have.has(norm(s.label)));
  const addFreeText = () => { const t = term.trim(); if (t) add.mutate({ item_id: null, label: t }); };
  const nothingYet = list.length === 0 && included.length === 0 && !includedLoading;

  return (
    <div className={cn(!embedded && "rounded-xl border border-hairline bg-white p-4 shadow-e1")}>
      {!embedded && (
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Package className="h-3.5 w-3.5" /></span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Parts the Plan Includes</span>
        </div>
      )}

      <div className="space-y-1.5">
        <IncludedPartRows parts={included} loading={includedLoading} />
        {list.map((p) => {
          const isCode = /^[A-Z0-9][A-Z0-9 -]{1,14}$/i.test(p.label);
          const name = isCode ? codeNames?.[norm(p.label)] ?? null : null;
          const usual = isPlanConsumable({ key: p.label, label: p.label, kind: ((p.kind as PartKind) ?? planPartKind(p.label)) });
          // Where it came from, in words a dispatcher reads: the ESC agreement is
          // the official list; everything here was added in Pegasus — picked from
          // the pricebook, typed, or guessed from an old call note (the seed).
          const from = p.source === "notes" ? "from an old call note" : p.source === "pricebook" ? "from the pricebook" : "added in Pegasus";
          return (
            <div key={p.id} className={cn("flex items-center gap-3 rounded-xl border px-3.5 py-2", usual ? "border-hairline" : "border-amber-200 bg-amber-50/40")}>
              <input type="number" min={1} value={p.qty}
                onChange={(e) => setQty.mutate({ id: p.id, qty: Math.max(1, Number(e.target.value) || 1) })}
                className="w-12 rounded-md border border-hairline bg-white px-1.5 py-1 text-sm tabular-nums" />
              {isCode
                ? <span className={cn("rounded-md border px-2 py-0.5 font-mono text-[12.5px] font-bold", PART_CHIP[(p.kind as PartKind) ?? "part"] ?? PART_CHIP.part)}>{p.label}</span>
                : <span className="text-sm text-slate-800">{p.label}</span>}
              <span className="min-w-0 flex-1 text-sm text-slate-800">
                <span className="block truncate">{name ?? ""}</span>
                <span className="block truncate text-xs text-slate-400">{from}</span>
              </span>
              {usual
                ? <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"><Check className="h-3 w-3" /> Included</span>
                : <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700" title="Plans replace pads, media, filters and bulbs — this looks like a one-time part">Not a Usual Plan Part</span>}
              <button type="button" onClick={() => remove.mutate(p.id)} title="Remove from the plan" className={cn("shrink-0 text-[12.5px] font-semibold transition-colors", usual ? "text-slate-300 hover:text-red-500" : "text-amber-700 hover:text-red-600")}>{usual ? <X className="h-4 w-4" /> : "Remove"}</button>
            </div>
          );
        })}
      </div>
      {nothingYet && <p className="text-sm text-slate-400">No parts on this plan yet.</p>}

      {newSuggestions.length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-brand-200 bg-brand-50/40 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-brand-600">
            <Sparkles className="h-3 w-3" /> From This Home's History
          </div>
          <div className="flex flex-wrap gap-1.5">
            {newSuggestions.map((s) => (
              <button key={s.key} type="button"
                onClick={() => add.mutate({ item_id: null, label: s.label, kind: s.kind, source: "notes" })}
                className="inline-flex items-center gap-1 rounded-lg border border-brand-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-brand-50">
                <Plus className="h-3 w-3 text-brand-500" /> {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-start gap-2">
      <div className="relative min-w-0 flex-1">
        <input value={term} onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (results && results.length) add.mutate({ item_id: results[0].id, label: results[0].description || results[0].part_number || "Part", source: "pricebook" }); else addFreeText(); } }}
          placeholder="Add a part — a filter size, a pad, a pricebook item"
          className="w-full rounded-xl border border-hairline px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" />
        {term.trim().length >= 2 && results && results.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-hairline bg-white shadow-lg">
            {results.map((it) => (
              <button key={it.id} type="button" onClick={() => add.mutate({ item_id: it.id, label: it.description || it.part_number || "Part", source: "pricebook" })} className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span className="font-medium text-slate-700">{it.description || it.part_number}</span>
                {it.part_number && <span className="ml-1.5 font-mono text-[11px] text-slate-400">{it.part_number}</span>}
              </button>
            ))}
            <button type="button" onClick={addFreeText} className="block w-full border-t border-slate-100 px-3 py-2 text-left text-xs text-slate-500 hover:bg-slate-50">+ Add “{term.trim()}” as typed</button>
          </div>
        )}
      </div>
        {/* PRICEBOOK-PICK-01: the full pricebook window — filters and details. */}
        <button type="button" onClick={() => setBrowse(true)} title="Open the pricebook — filters and details"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <BookOpen className="h-4 w-4" /> Browse
        </button>
      </div>
      <PricebookBrowser open={browse} onClose={() => setBrowse(false)} initialQuery={term} pickLabel="Add to Plan"
        onPick={(it) => add.mutate({ item_id: it.id, label: it.description || it.part_number || "Part", source: "pricebook" })} />
    </div>
  );
}
